import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement>;

/**
 * Settings section icons — dot-matrix aesthetic.
 *
 * Same language as the BottomTabBar icon pack in `./icons.tsx`: pure
 * filled circles, no strokes, `currentColor` fill. Displayed at 16×16
 * inside <SectionHeader> but drawn on a 24×24 viewBox for decent
 * detail. Each icon is a recognizable silhouette built from 5–15 dots.
 */

const baseProps: IconProps = {
  width: 16,
  height: 16,
  viewBox: '0 0 24 24',
  fill: 'none',
  'aria-hidden': true,
};

/** Wallet — 4×3 dot grid + one larger "chip" dot (card / store of value). */
export function WalletIcon(props: IconProps) {
  return (
    <svg {...baseProps} {...props}>
      {[0, 1, 2, 3].map((col) =>
        [0, 1, 2].map((row) => (
          <circle
            key={`${col}-${row}`}
            cx={5 + col * 5}
            cy={7 + row * 5}
            r={row === 1 && col === 3 ? 1.7 : 1.15}
            fill="currentColor"
          />
        )),
      )}
    </svg>
  );
}

/** Eye — almond of dots + emphasized pupil (watch addresses). */
export function EyeIcon(props: IconProps) {
  const LID = [
    [8, 8], [12, 7], [16, 8],
    [8, 16], [12, 17], [16, 16],
    [7, 12], [17, 12],
  ] as const;
  return (
    <svg {...baseProps} {...props}>
      {LID.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="1.2" fill="currentColor" />
      ))}
      <circle cx="12" cy="12" r="2.2" fill="currentColor" />
    </svg>
  );
}

/** Shield — 10 dots tracing a shield silhouette (security). */
export function ShieldIcon(props: IconProps) {
  const DOTS = [
    [12, 4, 1.5],
    [7, 6, 1.3],  [17, 6, 1.3],
    [5, 10, 1.2], [19, 10, 1.2],
    [7, 14, 1.2], [17, 14, 1.2],
    [10, 18, 1.2], [14, 18, 1.2],
    [12, 20, 1.3],
  ] as const;
  return (
    <svg {...baseProps} {...props}>
      {DOTS.map(([x, y, r], i) => (
        <circle key={i} cx={x} cy={y} r={r} fill="currentColor" />
      ))}
    </svg>
  );
}

/** Sun — big center dot + 8 ray dots around (appearance / theme). */
export function SunIcon(props: IconProps) {
  const RAYS = [
    [12, 4], [18, 6], [20, 12], [18, 18],
    [12, 20], [6, 18], [4, 12], [6, 6],
  ] as const;
  return (
    <svg {...baseProps} {...props}>
      <circle cx="12" cy="12" r="2.6" fill="currentColor" />
      {RAYS.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="1.3" fill="currentColor" />
      ))}
    </svg>
  );
}

/** Globe — 8-dot outer ring + meridian hints + emphasized equator (network). */
export function GlobeIcon(props: IconProps) {
  const RING = [
    [12, 4], [18, 6], [20, 12], [18, 18],
    [12, 20], [6, 18], [4, 12], [6, 6],
  ] as const;
  return (
    <svg {...baseProps} {...props}>
      {RING.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="1.2" fill="currentColor" />
      ))}
      <circle cx="12" cy="12" r="1.8" fill="currentColor" />
      <circle cx="12" cy="8" r="1.0" fill="currentColor" />
      <circle cx="12" cy="16" r="1.0" fill="currentColor" />
    </svg>
  );
}

/** Info — stylized "i" (about). */
export function InfoIcon(props: IconProps) {
  return (
    <svg {...baseProps} {...props}>
      <circle cx="12" cy="5" r="1.7" fill="currentColor" />
      <circle cx="12" cy="10" r="1.3" fill="currentColor" />
      <circle cx="12" cy="13" r="1.3" fill="currentColor" />
      <circle cx="12" cy="16" r="1.3" fill="currentColor" />
      <circle cx="10" cy="18.5" r="1.0" fill="currentColor" />
      <circle cx="14" cy="18.5" r="1.0" fill="currentColor" />
    </svg>
  );
}

/** Trash — handle + lid line + 3×3 body grid (danger zone). */
export function TrashIcon(props: IconProps) {
  return (
    <svg {...baseProps} {...props}>
      {/* Handle */}
      <circle cx="10" cy="4" r="1.1" fill="currentColor" />
      <circle cx="14" cy="4" r="1.1" fill="currentColor" />
      {/* Lid */}
      <circle cx="6" cy="7" r="1.2" fill="currentColor" />
      <circle cx="12" cy="7" r="1.45" fill="currentColor" />
      <circle cx="18" cy="7" r="1.2" fill="currentColor" />
      {/* Body — 3 columns × 3 rows */}
      {[11, 15, 19].map((y) => (
        <g key={y}>
          <circle cx="8" cy={y} r="1.15" fill="currentColor" />
          <circle cx="12" cy={y} r="1.15" fill="currentColor" />
          <circle cx="16" cy={y} r="1.15" fill="currentColor" />
        </g>
      ))}
    </svg>
  );
}
